import { IOptionProps } from '@amobee/component-library';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { cloneDeep } from 'lodash';
import { pairwise, startWith } from 'rxjs/operators';
import { CampaignVO } from 'src/app/data/vo/CampaignVO';
import { PrebuiltCampaignVO } from 'src/app/data/vo/PrebuiltCampaignVO';
import { BudgetingService } from 'src/app/services/budgeting.service';
import { ModalService } from 'src/app/services/modal.service';
import { environment } from 'src/environments/environment';
import { CampaignAdapter } from '../../../../../../adapters/campaign-adapter';
import { CampaignService } from '../../services/campaign.service';

@Component({
  selector: 'campaign-details',
  templateUrl: './campaign-details.component.html',
  styleUrls: ['./campaign-details.component.scss']
})
export class CampaignDetailsComponent implements OnInit {

  @Input() group: FormGroup;
  @Input() isNew: boolean;
  @Input() campaignVO: CampaignVO;
  @Input() prebuiltCampaignVOs: Array<PrebuiltCampaignVO>;
  @Input() segments: Array<any>;
  @Input() isReadOnly: boolean;
  // Datepicker will have form control in the format of { date: TimeStamp, allDay: boolean }, cannot use start/end date form control directly
  selectedStartDate: FormControl = new FormControl({});
  selectedEndDate: FormControl = new FormControl({});
  campaignDescription: string = "";
  disabledDayMessage: string = "We have not received necessary supply data to generate a proposal for this broadcast week. Please reach out to your account representative for more information.";

  // formControl to handle suggestion campaign selection on confirm/cancel of modal.
  suggestedCampaign: FormControl = new FormControl();
  oldSuggestedCampaignValue: any = null;
  newSuggestedCampaignValue: any;
  minStartDate: Date;
  maxStartDate: Date;
  minEndDate: Date;
  maxEndDate: Date;
  // due to API slowness, we are asked to refresh the dates twice, one at init and one when user clicks either of the datepicker
  datesRefreshed = false;
  templateAudienceData: { segments: any; languages: any; channels: any; };

  constructor(
    private modal: ModalService,
    private campaignAdapter: CampaignAdapter,
    private budgetingService: BudgetingService,
    private campaignService: CampaignService
  ) { }

  ngOnInit() {
    setTimeout(() => this.getDates(), 500);
    if (this.startDate.value) {
      this.selectedStartDate.patchValue({ date: this.budgetingService.convertFromLocalTime(this.startDate.value, environment.timezone, 'startDate').getTime() / 1000 });
    }

    if (this.endDate.value) {
      this.selectedEndDate.patchValue({ date: this.budgetingService.convertFromLocalTime(this.endDate.value, environment.timezone, 'endDate').getTime() / 1000 });
    }
    if (this.group.disabled || this.isReadOnly) {
      this.selectedStartDate.disable();
      this.selectedEndDate.disable();
      this.suggestedCampaign.disable();
    }
    //To get the old and new value of suggested campaigns.
    this.suggestedCampaign.valueChanges.pipe(pairwise()).subscribe(([oldVal, newVal]: [any, any]) => {
      this.oldSuggestedCampaignValue = oldVal;
      this.newSuggestedCampaignValue = newVal;
    });
  }

  setStartDate(event: any) {
    this.startDate.markAsTouched();
    let localDate = this.budgetingService.convertToLocalTime(event.date * 1000, environment.timezone, 'startDate').getTime();
    this.startDate.patchValue(localDate);
    this.minEndDate = new Date(event.date * 1000 + 86400000);
  }

  setEndDate(event: any) {
    this.endDate.markAsTouched();
    let localDate = this.budgetingService.convertToLocalTime(event.date * 1000, environment.timezone, 'endDate').getTime();
    this.endDate.patchValue(localDate);
  }

  refreshAvailableDates() {
    if (!this.datesRefreshed && this.isNew) {
      this.datesRefreshed = true;
      this.getDates(true);
    }
  }

  getDates(refresh: boolean = false) {
    // Set 7 and 8 days after date for StartDate and endDate respectively
    this.minStartDate = new Date(new Date().getTime() + 7 * 86400000);
    this.minEndDate = new Date(this.minStartDate.getTime() + 86400000);
    this.campaignAdapter.getAvailableFlightDateRange(this.campaignVO.id, !refresh).then((resp) => {
      if (resp) {
        if (resp.minStartDate && resp.minStartDate.getTime() > this.minStartDate.getTime()) {
          this.minStartDate = resp.minStartDate;
          this.minEndDate = new Date(this.minStartDate.getTime() + 86400000);
        }
        if (resp.maxEndDate) {
          this.maxEndDate = resp.maxEndDate;
          this.maxStartDate = new Date(this.maxEndDate.getTime() - 86400000);
        }
      }
    });
  }

  get name(): FormControl {
    return this.group.get('name') as FormControl;
  }

  get startDate(): FormControl {
    return this.group.get('startDate') as FormControl;
  }

  get endDate(): FormControl {
    return this.group.get('endDate') as FormControl;
  }

  prebuiltCampaignSelect(event: IOptionProps) {
    this.modal.confirmation({ message: 'Applying the suggested campaign template will overwrite your current campaign settings. Are you sure you want to continue?' }, '', '',
      () => {
        if (!event || !event.value) {
          this.campaignDescription = '';
          this.campaignService.setSuggestedCampaign(this.suggestedCampaign, null);
          return;
        }
        for (let pcvo of this.prebuiltCampaignVOs) {
          if (pcvo.name === event.value) {
            let cvo = cloneDeep(pcvo.cvo);
            //it should be okay to delete the original templates since the segments won't change in the same session
            if (cvo.segment) {
              let segment = this.segments.find(e => e.value === cvo.segment.value);
              if (segment) {
                cvo.segment = {
                  name: segment.name,
                  value: segment.value
                };
              } else {
                delete cvo.segment;
              }
            }
            this.campaignDescription = pcvo.description;
            if (cvo.goalType) {
              cvo.autoSelect = false;
            }
            this.campaignService.setSuggestedCampaign(this.suggestedCampaign, cvo);
            this.group.patchValue(cvo);
            this.group.markAsTouched();
            break;
          }
        }
      },
      () => {
        this.suggestedCampaign.patchValue(this.oldSuggestedCampaignValue);
      },
      undefined,
      () => {
        this.suggestedCampaign.patchValue(this.oldSuggestedCampaignValue);
      }
    );
  }

  onNameChange(event) {
    if (typeof event === 'string') {
      return;
    }
    if (event.target.value && event.target.value.length) {
      this.campaignVO.name = event.target.value;
      this.campaignAdapter.updateCampaignName(this.campaignVO);
    }
  }

}
